import type { ConfiguratorDefinition } from "../../common/contract/definition";
import { EMA100_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { EMA100_CSTICS } from "./ema100Cstics";

export const EMA100_CONFIGURATOR_DEFINITION = {
  productCode: EMA100_CONFIGURATOR_PRODUCT_CODE,
  titleKey: "configurator.actuator.name",
  stepResetScope: "stepAndSubsequent",
  steps: [
    {
      id: "linearActuator",
      titleKey: "configurator.actuator.step.1.1.name",
      descriptionKey: "configurator.actuator.series.EMA100.description",
      substeps: [
        {
          titleKey: "configurator.actuator.series.EMA100.name",
          descriptionKey: "configurator.actuator.series.EMA100.description",
          controls: [
            {
              type: "Hero",
              cstic: EMA100_CSTICS.seriesHero,
            },
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.actuatorSeries,
              tileLayout: "fill",
              hideLabel: true,
            },
          ],
        },
        {
          controls: [
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.actuatorType,
              tileLayout: "compact",
            },
          ],
        },
        {
          controlsLayout: { type: "grid", columns: 2 },
          controls: [
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.screwDiameterAndLead,
              hideDisabled: true,
            },
          ],
        },
        {
          // V2 has no composite dropdown/free-input control or dynamic stroke-range line,
          // so the standard and custom stroke CSTICs are exposed independently.
          controlsLayout: { type: "grid", columns: 2 },
          controls: [
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.standardStroke,
              hideDisabled: true,
            },
            {
              type: "NumberInput",
              cstic: EMA100_CSTICS.customStroke,
            },
          ],
        },
        {
          controls: [
            {
              type: "HorizontalCarousel",
              cstic: EMA100_CSTICS.screwType,
              tileLayout: "compact",
              hideDisabled: true,
            },
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.strokeOrientation,
              tileLayout: "compact",
              hideDisabled: true,
            },
          ],
        },
      ],
    },
    {
      id: "mountingOptions",
      titleKey: "configurator.actuator.step.1.2.name",
      substeps: [
        {
          titleKey: "configurator.actuator.section.mountingOptions.name",
          controls: [
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.frontMounting,
              hideDisabled: true,
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.frontParallel,
              hideDisabled: true,
              visibleWhen: { cstic: { name: EMA100_CSTICS.frontMounting }, valueIn: ["1"] },
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.frontFlange,
              hideDisabled: true,
              visibleWhen: { cstic: { name: EMA100_CSTICS.frontMounting }, valueIn: ["2"] },
            },
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.frontPivot,
              hideDisabled: true,
              visibleWhen: { cstic: { name: EMA100_CSTICS.frontMounting }, valueIn: ["3"] },
            },
          ],
        },
        {
          controls: [
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.housingOrientation,
            },
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.rearMounting,
              hideDisabled: true,
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.rearParallel,
              hideDisabled: true,
              visibleWhen: { cstic: { name: EMA100_CSTICS.rearMounting }, valueIn: ["1"] },
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.rearFlange,
              hideDisabled: true,
              visibleWhen: { cstic: { name: EMA100_CSTICS.rearMounting }, valueIn: ["2"] },
            },
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.rearPivot,
              hideDisabled: true,
              visibleWhen: { cstic: { name: EMA100_CSTICS.rearMounting }, valueIn: ["3"] },
            },
          ],
        },
      ],
    },
    {
      id: "actuatorOptions",
      titleKey: "configurator.actuator.step.1.3.name",
      substeps: [
        {
          controlsLayout: { type: "grid", columns: 2 },
          controls: [
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.support,
              hideDisabled: true,
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.visualInspection,
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.bellows,
              hideDisabled: true,
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.sensorOption,
              hideDisabled: true,
            },
          ],
        },
      ],
    },
    {
      id: "endOptions",
      titleKey: "configurator.actuator.step.1.4.name",
      substeps: [
        {
          controls: [
            {
              type: "TileSelect",
              cstic: EMA100_CSTICS.endOption,
              tileLayout: "compact",
              hideDisabled: true,
            },
            {
              type: "DropdownSelect",
              cstic: EMA100_CSTICS.lubrication,
              hideDisabled: true,
            },
            {
              // V2 TileSelect cannot configure horizontal scrolling; compact wrapped tiles are used.
              type: "TileSelect",
              cstic: EMA100_CSTICS.limitSwitch,
              tileLayout: "compact",
              hideDisabled: true,
            },
          ],
        },
      ],
    },
  ],
} satisfies ConfiguratorDefinition;
